import React, {
  MouseEvent,
  useCallback,
  useEffect,
} from 'react';
import clsx from 'clsx';
import Button from 'pages/Dashboard/components/Button';
import Spinner from 'pages/Dashboard/components/Spinner';
import Skeleton from 'core/components/Skeleton';
import {
  useNotify,
} from 'core/hooks';
import {
  useGetRTMPatientStatus,
  useSendCheckInSurvey,
  useSendEnrollmentInvitation,
} from 'pages/Dashboard/pages/Dashboard/RTM/hooks';
import {
  rtmEventChannel,
} from 'pages/Dashboard/services/eventChannels';

type Props = {
  patientId: number;
  disabled?: boolean;
  className?: string;
};

type ActionButtonProps = Props & {
  label: string;
  successMessage: string;
  onSend: (params: { patientId: number }) => Promise<unknown>;
  isLoading: boolean;
};

function RTMActionButton({
  patientId,
  disabled,
  className,
  label,
  successMessage,
  onSend,
  isLoading,
}: ActionButtonProps) {
  const notify = useNotify();

  const handleClick = useCallback(async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      await onSend({ patientId });
      rtmEventChannel.emit('onRTMPatientDataChange', patientId);
      notify({
        variant: 'success',
        message: successMessage,
      });
    } catch {
      // Global mutation onError handles error notify
    }
  }, [notify, onSend, patientId, successMessage]);

  return (
    <Button
      className={clsx(
        '!py-0.5 !px-0 !min-w-[160px]',
        { '!border-blue-500': !isLoading },
        className,
      )}
      variant="outlined"
      type="button"
      color="primary"
      onClick={handleClick}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <div className="gap-2 flex items-center">
          <Spinner size={16} />
          Sending...
        </div>
      ) : label}
    </Button>
  );
}

export default function SendRTMActionButton({
  patientId,
  disabled,
  className,
}: Props) {
  const {
    data: rtmStatus,
    isLoading: isLoadingStatus,
    refetch,
  } = useGetRTMPatientStatus(patientId);
  const {
    mutateAsync: sendEnrollmentInvitation,
    isPending: isSendingEnrollment,
  } = useSendEnrollmentInvitation();
  const {
    mutateAsync: sendCheckInSurvey,
    isPending: isSendingCheckIn,
  } = useSendCheckInSurvey();

  useEffect(() => (
    rtmEventChannel.on('onRTMPatientDataChange', (targetPatientId) => {
      if (targetPatientId === patientId) {
        refetch();
      }
    })
  ), [patientId, refetch]);

  const isEnrollmentComplete = rtmStatus?.enrollment?.isComplete === true;
  const action = isEnrollmentComplete
    ? {
      label: 'Send Check in',
      successMessage: 'Check-in survey sent.',
      onSend: sendCheckInSurvey,
      isLoading: isSendingCheckIn,
    }
    : {
      label: 'Send Enrollment',
      successMessage: 'Enrollment invitation sent.',
      onSend: sendEnrollmentInvitation,
      isLoading: isSendingEnrollment,
    };

  return isLoadingStatus ? (
    <div className="gap-2 flex items-center w-full">
      <Skeleton className="!w-52 !h-5" />
    </div>
  ) : (
    <RTMActionButton
      patientId={patientId}
      disabled={disabled}
      className={className}
      label={action.label}
      successMessage={action.successMessage}
      onSend={action.onSend}
      isLoading={action.isLoading}
    />
  );
}
